import React, { useEffect, useRef, useImperativeHandle, forwardRef, useState } from 'react';
import SignaturePad from 'signature_pad';
import { Eraser } from 'lucide-react';

const SignaturePadField = forwardRef(function SignaturePadField(
  { onEndStroke },
  ref
) {
  const canvasRef = useRef(null);
  const padRef = useRef(null);
  const containerRef = useRef(null);
  const onEndStrokeRef = useRef(onEndStroke);
  const [isEmpty, setIsEmpty] = useState(true);

  useEffect(() => {
    onEndStrokeRef.current = onEndStroke;
  }, [onEndStroke]);

  useImperativeHandle(ref, () => ({
    clear: () => {
      padRef.current?.clear();
      setIsEmpty(true);
    },
    isEmpty: () => padRef.current?.isEmpty() ?? true,
    toDataURL: () => {
      if (!padRef.current || padRef.current.isEmpty()) return null;
      return padRef.current.toDataURL('image/png');
    },
  }));

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const resize = () => {
      const ratio = Math.max(window.devicePixelRatio || 1, 1);
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width * ratio;
      canvas.height = rect.height * ratio;
      const ctx = canvas.getContext('2d', { willReadFrequently: true });
      ctx.scale(ratio, ratio);
      // Re-init or clear pad after resize to avoid distorted state
      if (padRef.current) {
        padRef.current.clear();
        setIsEmpty(true);
      }
    };

    resize();

    const pad = new SignaturePad(canvas, {
      penColor: '#0e7490',
      backgroundColor: 'rgba(255,255,255,0)',
      minWidth: 1.2,
      maxWidth: 3.0,
      throttle: 8,
      velocityFilterWeight: 0.7,
    });
    padRef.current = pad;

    const handleEnd = () => {
      setIsEmpty(pad.isEmpty());
      if (onEndStrokeRef.current) onEndStrokeRef.current(!pad.isEmpty());
    };
    pad.addEventListener('endStroke', handleEnd);

    window.addEventListener('resize', resize);
    return () => {
      pad.removeEventListener('endStroke', handleEnd);
      pad.off();
      window.removeEventListener('resize', resize);
    };
  }, []);

  const handleClear = () => {
    padRef.current?.clear();
    setIsEmpty(true);
    if (onEndStrokeRef.current) onEndStrokeRef.current(false);
  };

  return (
    <div className="w-full" data-testid="signature-pad-container">
      <div
        ref={containerRef}
        className="relative rounded-3xl border-2 border-blue-500/30 bg-white overflow-hidden shadow-inner"
        style={{ height: '240px' }}
      >
        <canvas
          ref={canvasRef}
          className="sig-canvas"
          data-testid="signature-canvas"
        />
        {isEmpty && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <span className="font-display italic text-blue-400/80 text-3xl">
              Sign here
            </span>
          </div>
        )}
        <div className="pointer-events-none absolute bottom-3 left-6 right-6 border-b-2 border-dashed border-blue-500/30" />
      </div>
      <div className="mt-3 flex items-center justify-between">
        <p className="text-sm text-blue-300/70 tracking-wide">
          Use your finger or stylus to sign above.
        </p>
        <button
          type="button"
          onClick={handleClear}
          className="pill-btn inline-flex items-center gap-2 h-12 px-5 rounded-full bg-slate-900/70 border border-blue-500/30 text-blue-300 font-semibold hover:bg-slate-800 active:scale-95"
          data-testid="signature-clear-button"
        >
          <Eraser className="w-4 h-4" />
          Erase
        </button>
      </div>
    </div>
  );
});

export default SignaturePadField;
